/**
 * Labels and small lookups for the satellite controls, info bar and legend.
 */

import { formatGoesClock, goesLegendStops, goesProduct } from "../data/goesColor.js";

const SECTOR_LABELS = {
    M1: "Meso 1", M2: "Meso 2", M: "Meso", C: "CONUS", F: "Full disk", AUTO: "Auto",
};

// Picker groups. The GeoColor products sit above them on their own.
export const SAT_PRODUCT_GROUPS = Object.freeze([
    { label: "Visible", ids: ["1", "2", "3"] },
    { label: "Near-IR", ids: ["4", "5", "6"] },
    { label: "Water vapor", ids: ["8", "9", "10"] },
    { label: "Infrared", ids: ["7", "11", "12", "13", "14", "15", "16"] },
]);

export const SAT_QUICK_PRODUCTS = Object.freeze([
    { id: "geo", label: "GeoColor" },
    { id: "geosr", label: "SuperRes" },
    { id: "13", label: "Clean IR" },
    { id: "9", label: "Mid WV" },
]);

export function satSectorLabel(sector) {
    return SECTOR_LABELS[String(sector || "").trim().toUpperCase()] || "";
}

export function satProductLabel(product) {
    return goesProduct(product)?.label || "GeoColor";
}

// "G16 CONUS · Clean IR · 22:01Z"
export function formatSatInfo(frame) {
    if (!frame) return "";
    const source = [frame.sat, satSectorLabel(frame.sector)].filter(Boolean).join(" ");
    const clock = formatGoesClock(frame.startMs);
    return [source, satProductLabel(frame.product), clock].filter(Boolean).join(" · ");
}

// Satellite scan start minus radar time: "−1 min vs radar".
export function formatSatOffset(satMs, radarMs) {
    if (!Number.isFinite(satMs) || !Number.isFinite(radarMs)) return "";
    const minutes = Math.round((satMs - radarMs) / 60000);
    if (minutes === 0) return "0 min vs radar";
    return `${minutes > 0 ? "+" : "−"}${Math.abs(minutes)} min vs radar`;
}

export function nearestByTime(items, ms, accept = () => true) {
    let best = null;
    let bestGap = Infinity;
    for (const item of items || []) {
        if (!item || !Number.isFinite(item.ms) || !accept(item)) continue;
        const gap = Math.abs(item.ms - ms);
        if (gap < bestGap) {
            best = item;
            bestGap = gap;
        }
    }
    return best;
}

function signedC(c) {
    if (c === 0) return "0";
    return c > 0 ? `+${c}` : `−${Math.abs(c)}`;
}

// Legend config in the PRODUCT_LEGEND_CONFIG shape (items and ticks listed
// warm → cold, the way radar lists high → low). Null when a band has no scale.
export function satLegendConfig(product) {
    const spec = goesProduct(product);
    const legend = spec && goesLegendStops(spec.id);
    if (!legend) return null;
    const items = legend.colors
        .slice()
        .reverse()
        .map(({ c, rgb }) => ({ label: `${signedC(c)} °C`, color: `rgb(${rgb[0]},${rgb[1]},${rgb[2]})` }));
    const ticks = [];
    const tickStep = (legend.maxC - legend.minC) / 4;
    for (let c = legend.maxC; c >= legend.minC - 1e-9; c -= tickStep) ticks.push(signedC(Math.round(c)));
    ticks[ticks.length - 1] += " °C";
    return {
        label: `${spec.label} (band ${spec.id})`,
        code: `C${spec.id.padStart(2, "0")}`,
        items,
        ticks,
    };
}
